import React from 'react'
import { Stack, DialogActions as MuiDialogActions, Button, useTheme, Box, useMediaQuery } from '@mui/material'
import { ButtonWithLoading } from '../Buttons/Buttons'
import DialogPagination, { DialogPaginationProps } from './DialogPagination'
import { DialogActions } from './Dialog'
import { makeStyles } from 'tss-react/mui'
import { Typography } from '../Typography'

const useStyles = makeStyles()(theme => ({
  dialogActions: {
    paddingBlock: theme.spacing(2),
    paddingInline: theme.spacing(4),
    gap: theme.spacing(2),

    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
    },
  },
  container: {
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
  },
  leftActionSpacer: {
    flex: '1 0 0',
  },
  leftActionButton: {
    '& > *:not(:last-of-type)': {
      marginRight: theme.spacing(1),
    },
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
  },
}))

interface Props {
  setOpen: (isOpen: boolean) => void
  actions?: DialogActions
  additionalActionsLeft?: React.ReactNode
  pagination?: DialogPaginationProps
}

/**
 * Actions are displayed at the bottom of a dialog and are either primary or secondary.
 */
const Actions: React.FC<Props> = ({ setOpen, additionalActionsLeft, pagination, actions }) => {
  const { classes } = useStyles()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const secondaryActions = Array.isArray(actions?.secondary) ? actions?.secondary : actions?.secondary ? [actions.secondary] : []

  return (
    <MuiDialogActions className={classes.dialogActions}>
      {additionalActionsLeft && (
        <>
          <div className={classes.leftActionButton}>{additionalActionsLeft}</div>
          {!isMobile && <div className={classes.leftActionSpacer}></div>}
        </>
      )}

      {pagination && <DialogPagination count={pagination.count} page={pagination.page} setPage={pagination.setPage} />}

      <Stack className={classes.container} direction={isMobile ? 'column' : 'row'} spacing={2}>
        {actions?.secondary &&
          secondaryActions.map(action => (
            <Box key={action.name}>
              <Button
                color={'inherit'}
                sx={{ color: theme.shape.secondaryAction.color, borderColor: theme.shape.secondaryAction.borderColor }}
                variant={'outlined'}
                onClick={() => {
                  action.onClick ? action.onClick() : setOpen(false)
                }}
                data-qa={`button-modal-secondary-${action.name}`}
                disabled={action.disabled}
                fullWidth={isMobile}
              >
                <Typography variant={'button'}>{action.name}</Typography>
              </Button>
            </Box>
          ))}
        {actions?.additional && (
          <ButtonWithLoading
            isLoading={Boolean(actions.additional.isLoading)}
            onClick={() => actions.additional?.onClick?.() ?? setOpen(true)}
            disabled={actions.additional.disabled}
            label={actions.additional.name}
            dataQa={'button-modal-additional'}
            fullWidth={isMobile}
          />
        )}
        {actions?.primary && (
          <ButtonWithLoading
            isLoading={Boolean(actions.primary.isLoading)}
            onClick={() => actions.primary?.onClick?.() ?? setOpen(true)}
            disabled={actions.primary.disabled}
            label={actions.primary.name}
            dataQa={'button-modal-primary'}
            fullWidth={isMobile}
          />
        )}
      </Stack>
    </MuiDialogActions>
  )
}

export default Actions
